'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Bird, TrendingDown, TrendingUp, ArrowRightLeft, Skull, ShoppingCart, AlertTriangle, Plus, FileText, Filter, ChevronDown, Eye, ArrowRight, Activity, BarChart3, CheckCircle2, XCircle, Layers, Sliders } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { KPICard, ChartCard, Card } from '@/components/ui/Cards';
import { MOCK_SUMMARY, MOCK_FLOCK_SUMMARIES, MOCK_MOVEMENTS, MOCK_POPULATION_TREND, MOCK_ALERTS, MOVEMENT_TYPE_LABELS, MOVEMENT_TYPE_COLORS, type FlockPopulationSummary, type BirdAlert, type BirdMovement } from './birdRecordsData';

// ─── Status Badge ─────────────────────────────────────────────────────────────

function MovementStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-600' },
    pending_approval: { label: 'Pending', cls: 'bg-amber-100 text-amber-700' },
    approved: { label: 'Approved', cls: 'bg-blue-100 text-blue-700' },
    posted: { label: 'Posted', cls: 'bg-green-100 text-green-700' },
    reversed: { label: 'Reversed', cls: 'bg-purple-100 text-purple-700' },
    rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
    cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500' },
  };
  const s = map[status] ?? { label: status, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

function FlockStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: { label: 'Active', cls: 'bg-green-100 text-green-700' },
    completed: { label: 'Completed', cls: 'bg-blue-100 text-blue-700' },
    closed: { label: 'Closed', cls: 'bg-gray-100 text-gray-600' },
    sold: { label: 'Sold', cls: 'bg-purple-100 text-purple-700' },
  };
  const s = map[status] ?? { label: status, cls: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

// ─── Alert Card ───────────────────────────────────────────────────────────────

function AlertItem({ alert }: { alert: BirdAlert }) {
  const isDanger = alert.severity === 'danger';
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${isDanger ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
      <AlertTriangle size={14} className={`flex-shrink-0 mt-0.5 ${isDanger ? 'text-red-500' : 'text-amber-500'}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-xs font-semibold ${isDanger ? 'text-red-700' : 'text-amber-700'}`}>{alert.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{alert.message}</p>
        {alert.flockNumber && (
          <p className="text-xs text-muted-foreground mt-0.5">{alert.flockNumber} · {alert.house}</p>
        )}
      </div>
    </div>
  );
}

// ─── Direction Badge ──────────────────────────────────────────────────────────

function DirectionBadge({ direction }: { direction: string }) {
  if (direction === 'in') return <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700"><TrendingUp size={10} />IN</span>;
  if (direction === 'out') return <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600"><TrendingDown size={10} />OUT</span>;
  return <span className="text-xs font-medium text-muted-foreground">—</span>;
}

// ─── Population Waterfall ─────────────────────────────────────────────────────

const waterfallData = [
  { name: 'Opening', value: 31500, fill: '#2563eb' },
  { name: '+ Added', value: 0, fill: '#16a34a' },
  { name: '+ Transfers In', value: 200, fill: '#0891b2' },
  { name: '- Mortality', value: -312, fill: '#dc2626' },
  { name: '- Culled', value: -48, fill: '#b91c1c' },
  { name: '- Sold', value: 0, fill: '#9333ea' },
  { name: '- Transfers Out', value: -200, fill: '#d97706' },
  { name: 'Closing', value: 31140, fill: '#15803d' },
];

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function BirdRecordsDashboard() {
  const [trendRange, setTrendRange] = useState<'7d' | '30d' | 'month'>('30d');
  const [filterFarm, setFilterFarm] = useState('All Farms');
  const [showFilters, setShowFilters] = useState(false);

  const summary = MOCK_SUMMARY;
  const recentMovements = MOCK_MOVEMENTS.slice(0, 8).reverse();

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Bird Records</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Track bird population, movements and daily changes across farms, houses and flocks.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border/60 rounded-lg bg-card hover:bg-muted/50 transition-colors"
          >
            <Filter size={12} />
            Filters
            <ChevronDown size={12} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          <Link
            href="/bird-records/movements/new"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus size={12} />
            Record Movement
          </Link>
          <Link
            href="/bird-records/daily"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border/60 rounded-lg bg-card hover:bg-muted/50 transition-colors"
          >
            <FileText size={12} />
            Daily Record
          </Link>
        </div>
      </div>

      {/* ── Filters Panel ── */}
      {showFilters && (
        <Card padding="sm">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {['Farm', 'Branch', 'Poultry House', 'Flock', 'Bird Type', 'Status'].map((f) => (
              <div key={f}>
                <label className="text-xs text-muted-foreground block mb-1">{f}</label>
                <select className="w-full text-xs border border-border/60 rounded-lg px-2 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
                  <option>All</option>
                  {f === 'Farm' && <><option>Idudi Poultry Farm</option><option>Kampala Demo Farm</option></>}
                  {f === 'Bird Type' && <><option>Broiler</option><option>Layer</option><option>Breeder</option></>}
                  {f === 'Status' && <><option>Active</option><option>Completed</option><option>Closed</option></>}
                </select>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Alerts ── */}
      {MOCK_ALERTS.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {MOCK_ALERTS.map((a) => <AlertItem key={a.id} alert={a} />)}
        </div>
      )}

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-3">
        <KPICard
          label="Current Birds"
          value={summary.currentBirds.toLocaleString()}
          sub="All active flocks"
          icon={Bird}
          iconBg="#eff6ff"
          iconColor="#2563eb"
          change={summary.currentBirdsChange}
          changeLabel="vs last month"
          invertTrend
        />
        <KPICard
          label="Opening Population"
          value={summary.openingPopulation.toLocaleString()}
          sub="Start of period"
          icon={Layers}
          iconBg="#f0fdf4"
          iconColor="#15803d"
        />
        <KPICard
          label="Transfers In"
          value={summary.transfersIn.toLocaleString()}
          sub="Birds received"
          icon={TrendingUp}
          iconBg="#f0fdf4"
          iconColor="#059669"
        />
        <KPICard
          label="Transfers Out"
          value={summary.transfersOut.toLocaleString()}
          sub="Birds dispatched"
          icon={ArrowRightLeft}
          iconBg="#fff7ed"
          iconColor="#d97706"
        />
        <KPICard
          label="Mortality"
          value={summary.mortality.toLocaleString()}
          sub={`${summary.mortalityRate}% rate`}
          icon={Skull}
          iconBg="#fef2f2"
          iconColor="#dc2626"
          change={summary.mortalityChange}
          changeLabel="vs last month"
          invertTrend
          variant="danger"
        />
        <KPICard
          label="Culled Birds"
          value={summary.culled.toLocaleString()}
          sub="This period"
          icon={XCircle}
          iconBg="#fef2f2"
          iconColor="#b91c1c"
        />
        <KPICard
          label="Birds Sold"
          value={summary.birdsSold.toLocaleString()}
          sub="Confirmed sales"
          icon={ShoppingCart}
          iconBg="#faf5ff"
          iconColor="#9333ea"
        />
        <KPICard
          label="Adjustments"
          value={summary.adjustments.toLocaleString()}
          sub="Net adjustments"
          icon={Sliders}
          iconBg="#f0fdf4"
          iconColor="#16a34a"
        />
        <KPICard
          label="Closing Population"
          value={summary.closingPopulation.toLocaleString()}
          sub="End of period"
          icon={CheckCircle2}
          iconBg="#f0fdf4"
          iconColor="#15803d"
          variant="success"
        />
        <KPICard
          label="Survival Rate"
          value={`${summary.survivalRate}%`}
          sub="All active flocks"
          icon={Activity}
          iconBg="#f0fdf4"
          iconColor="#16a34a"
          variant="success"
        />
        <KPICard
          label="Mortality Rate"
          value={`${summary.mortalityRate}%`}
          sub="Cumulative"
          icon={TrendingDown}
          iconBg="#fef2f2"
          iconColor="#dc2626"
        />
        <KPICard
          label="Active Flocks"
          value={MOCK_FLOCK_SUMMARIES.filter(f => f.status === 'active').length}
          sub="Across all farms"
          icon={Layers}
          iconBg="#eff6ff"
          iconColor="#2563eb"
        />
      </div>

      {/* ── Charts Row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Population Trend */}
        <div className="lg:col-span-2">
          <ChartCard
            title="Population Overview"
            subtitle="Bird population movements over time"
            action={
              <div className="flex items-center gap-1">
                {(['7d', '30d', 'month'] as const).map((r) => (
                  <button
                    key={r}
                    onClick={() => setTrendRange(r)}
                    className={`px-2 py-1 text-xs rounded-md transition-colors ${trendRange === r ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50'}`}
                  >
                    {r === '7d' ? '7D' : r === '30d' ? '30D' : 'Month'}
                  </button>
                ))}
              </div>
            }
          >
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={MOCK_POPULATION_TREND} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="closingGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.5} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid var(--border)' }}
                  formatter={(v: number) => [v.toLocaleString(), '']}
                />
                <Area type="monotone" dataKey="closing" stroke="#2563eb" strokeWidth={2} fill="url(#closingGrad)" name="Closing" />
                <Area type="monotone" dataKey="mortality" stroke="#dc2626" strokeWidth={1.5} fill="none" name="Mortality" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Population Waterfall */}
        <ChartCard title="Population Breakdown" subtitle="Movement summary this period">
          <div className="space-y-1.5">
            {[
              { label: 'Opening', value: 31500, color: '#2563eb', sign: '' },
              { label: 'Transfers In', value: 200, color: '#059669', sign: '+' },
              { label: 'Mortality', value: 312, color: '#dc2626', sign: '-' },
              { label: 'Culled', value: 48, color: '#b91c1c', sign: '-' },
              { label: 'Sold', value: 0, color: '#9333ea', sign: '-' },
              { label: 'Transfers Out', value: 200, color: '#d97706', sign: '-' },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{row.label}</span>
                <span className="font-semibold font-tabular" style={{ color: row.color }}>
                  {row.sign}{row.value.toLocaleString()}
                </span>
              </div>
            ))}
            <div className="border-t border-border/60 pt-1.5 flex items-center justify-between text-xs">
              <span className="font-semibold text-foreground">Closing Population</span>
              <span className="font-bold font-tabular text-green-700">31,140</span>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* ── Flock Population Table ── */}
      <Card padding="sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Population by Flock</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Current bird counts across all active flocks</p>
          </div>
          <Link href="/bird-records/movements" className="text-xs text-primary hover:underline flex items-center gap-1">
            View All Movements <ArrowRight size={12} />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60">
                {['Flock #', 'Name', 'Type', 'Farm / House', 'Age', 'Initial', 'Current', 'Mortality', 'Culled', 'Survival %', 'Occupancy', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="text-left py-2 px-2 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MOCK_FLOCK_SUMMARIES.map((flock) => (
                <tr key={flock.flockId} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                  <td className="py-2 px-2 font-medium text-primary whitespace-nowrap">{flock.flockNumber}</td>
                  <td className="py-2 px-2 text-foreground whitespace-nowrap max-w-[140px] truncate">{flock.flockName}</td>
                  <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">{flock.birdType}</td>
                  <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">
                    <div>{flock.farm}</div>
                    <div className="text-muted-foreground/70">{flock.house}</div>
                  </td>
                  <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">
                    {flock.ageDays}d / {flock.ageWeeks}w
                  </td>
                  <td className="py-2 px-2 font-tabular text-foreground">{flock.initialBirds.toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular font-semibold text-foreground">{flock.currentBirds.toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular text-red-600">{flock.mortality.toLocaleString()}</td>
                  <td className="py-2 px-2 font-tabular text-orange-600">{flock.culled.toLocaleString()}</td>
                  <td className="py-2 px-2">
                    <span className={`font-tabular font-semibold ${flock.survivalRate >= 97 ? 'text-green-600' : flock.survivalRate >= 95 ? 'text-amber-600' : 'text-red-600'}`}>
                      {flock.survivalRate.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1.5">
                      <div className="w-16 bg-muted rounded-full h-1.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${flock.occupancyPct >= 95 ? 'bg-red-500' : flock.occupancyPct >= 85 ? 'bg-amber-500' : 'bg-green-500'}`}
                          style={{ width: `${flock.occupancyPct}%` }}
                        />
                      </div>
                      <span className="font-tabular text-muted-foreground">{flock.occupancyPct.toFixed(0)}%</span>
                    </div>
                  </td>
                  <td className="py-2 px-2"><FlockStatusBadge status={flock.status} /></td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1">
                      <Link href="/bird-records/movements" className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors" title="View Movements">
                        <Eye size={12} />
                      </Link>
                      <Link href="/bird-records/movements/new" className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors" title="Record Movement">
                        <Plus size={12} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Recent Movements ── */}
      <Card padding="sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Recent Bird Movements</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Latest population-changing transactions</p>
          </div>
          <Link href="/bird-records/movements" className="text-xs text-primary hover:underline flex items-center gap-1">
            Full Ledger <ArrowRight size={12} />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60">
                {['Movement #', 'Date', 'Type', 'Flock', 'Qty', 'Direction', 'Balance After', 'Recorded By', 'Status'].map((h) => (
                  <th key={h} className="text-left py-2 px-2 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentMovements.map((m) => (
                <tr key={m.id} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                  <td className="py-2 px-2 font-medium text-primary whitespace-nowrap">
                    <Link href={`/bird-records/movements`} className="hover:underline">{m.movementNumber}</Link>
                  </td>
                  <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">{m.movementDate}</td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: `${MOVEMENT_TYPE_COLORS[m.movementType]}18`,
                        color: MOVEMENT_TYPE_COLORS[m.movementType],
                      }}
                    >
                      {MOVEMENT_TYPE_LABELS[m.movementType]}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-foreground whitespace-nowrap">{m.flockNumber}</td>
                  <td className="py-2 px-2 font-tabular font-semibold text-foreground">{m.quantity.toLocaleString()}</td>
                  <td className="py-2 px-2"><DirectionBadge direction={m.direction} /></td>
                  <td className="py-2 px-2 font-tabular text-foreground">{m.populationAfter?.toLocaleString() ?? '—'}</td>
                  <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">{m.recordedBy}</td>
                  <td className="py-2 px-2"><MovementStatusBadge status={m.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Quick Actions ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Record Movement', icon: Plus, href: '/bird-records/movements/new', color: '#2563eb', bg: '#eff6ff' },
          { label: 'Daily Record', icon: FileText, href: '/bird-records/daily', color: '#059669', bg: '#f0fdf4' },
          { label: 'View Analytics', icon: BarChart3, href: '/bird-records/analytics', color: '#7c3aed', bg: '#faf5ff' },
          { label: 'Generate Report', icon: FileText, href: '/bird-records/reports', color: '#d97706', bg: '#fffbeb' },
        ].map((action) => (
          <Link
            key={action.label}
            href={action.href}
            className="flex items-center gap-2 p-3 rounded-xl border border-border/60 bg-card hover:shadow-md transition-all duration-200"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: action.bg }}>
              <action.icon size={14} style={{ color: action.color }} />
            </div>
            <span className="text-xs font-medium text-foreground">{action.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
